import * as EventGroupArtist from './EventGroupArtist';
import { ApolloClient, gql } from '@apollo/client/core';
import { Query } from '../../generated/graphql';
import { COLLECTION_METADATA_FIELDS, CollectionArguments } from '../collection/Api';
import * as GqlCollection from '../collection/GqlCollection';
import { VPResult } from '../VPResult';
import { ARTIST_FIELDS } from '../Artist/Api';

const EVENT_ARTISTS_QUERY = gql`
  query EventGroupArtists($eventGroupId: Int!, $page: Int, $limit: Int) {
    app {
      eventGroupArtists(eventGroupId: $eventGroupId, page: $page, limit: $limit) {
        collection {
          id
          eventGroupId
          legacyEventArtistId
          legacySettlementSheetId
          legacyOfferSheetId
          artist {
            ...ArtistFields
          }
        }
        metadata {
          ...CollectionMetadataFields
        }
      }
    }
  }
  ${COLLECTION_METADATA_FIELDS}
  ${ARTIST_FIELDS}
`;

export const fetchEventGroupArtists = async (client: ApolloClient<Query>, eventGroupId: number, collectionArguments: CollectionArguments = {}): Promise<GqlCollection.GqlCollection<EventGroupArtist.EventGroupArtist>> => {
  const { data } = await client.query({
    query: EVENT_ARTISTS_QUERY,
    variables: {
      ...collectionArguments,
      eventGroupId,
    },
  });

  return GqlCollection.fromGql(data.app.eventGroupArtists, EventGroupArtist.fromGqlCollection);
};

type AddEventGroupArtistParams = {
  eventGroupId: number;
  artist: {
    id?: number;
    name?: string;
    platformUrl?: string;
  };
};

const ADD_EVENT_ARTISTS_MUTATION = gql`
  mutation AddEventGroupArtist($eventGroupId: Int!, $artist: ArtistInput!) {
    app {
      addEventGroupArtist(input: { eventGroupId: $eventGroupId, artist: $artist}) {
        eventGroupArtist {
          id
          artistId
          eventGroupId
          legacyEventArtistId
          legacySettlementSheetId
          legacyOfferSheetId
          artist {
            ...ArtistFields
          }
        }
        clientMutationId
        errors
        success
      }
    }
  }
  ${ARTIST_FIELDS}
`;

export const addEventGroupArtist = async (client: ApolloClient<Query>, addParams: AddEventGroupArtistParams): Promise<VPResult<EventGroupArtist.EventGroupArtist>> => {
  const { data } = await client.mutate({
    mutation: ADD_EVENT_ARTISTS_MUTATION,
    variables: {
      ...addParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.addEventGroupArtist;
  if (response.success) {
    return {
      success: true,
      data: EventGroupArtist.fromGql(response),
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

const REMOVE_EVENT_GROUP_ARTIST_MUTATION = gql`
  mutation RemoveEventGroupArtist($eventGroupArtistId: Int!) {
    app {
      removeEventGroupArtist(input: { eventGroupArtistId: $eventGroupArtistId}) {
        errors
        success
      }
    }
  }
`;

export const removeEventGroupArtist = async (client: ApolloClient<Query>, eventGroupArtistId: number): Promise<VPResult<void>> => {
  const { data } = await client.mutate({
    mutation: REMOVE_EVENT_GROUP_ARTIST_MUTATION,
    variables: {
      eventGroupArtistId,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.app.removeEventGroupArtist;
  if (response.success) {
    return {
      success: true,
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};
